import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import {
  Plus, Search, Edit, Trash2, MoreHorizontal, Image as ImageIcon, 
  ChevronRight, Eye, EyeOff, Settings, Grid, List, Save, X,
  DollarSign, Tag, Clock, Flame, Leaf
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

export default function UnifiedMenuManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get token from outlet context (backoffice) or localStorage (admin app)
  const outletContext = useOutletContext() || {};
  const token = outletContext.token || localStorage.getItem('admin_token');
  
  // ALL useState hooks MUST be called before any conditional returns (React Rules of Hooks)
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('categories');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [modifierDialog, setModifierDialog] = useState(false);
  
  // Editing states
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingModifier, setEditingModifier] = useState(null);
  const [selectedItemForModifier, setSelectedItemForModifier] = useState(null);
  
  const [saving, setSaving] = useState(false);

  // Forms
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    image_url: '',
    sort_order: 0,
    is_active: true
  });

  const [itemForm, setItemForm] = useState({
    category_id: '',
    name: '',
    description: '',
    price: '',
    image_url: '',
    is_available: true,
    is_popular: false,
    is_spicy: false,
    is_vegetarian: false,
    is_vegan: false,
    is_gluten_free: false,
    calories: '',
    prep_time_minutes: 15,
    sort_order: 0
  });

  const [modifierForm, setModifierForm] = useState({
    name: '',
    is_required: false,
    min_selections: 0,
    max_selections: 1,
    options: [{ name: '', price: 0 }]
  });

  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      const [catRes, itemRes] = await Promise.all([
        axios.get(`${API}/menu-management/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/menu-management/items`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCategories(catRes.data);
      setItems(itemRes.data);
    } catch (error) {
      console.error('Failed to fetch menu data:', error);
      toast.error('Failed to load menu data');
    } finally {
      setLoading(false);
    }
  });

  // useEffect must also be called before conditional returns
  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);
  
  // Now we can safely do conditional return AFTER all hooks
  if (!token) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <p className="text-slate-700 mb-4">Authentication required</p>
          <button
            onClick={() => navigate('/admin/login')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // ==================== CATEGORY CRUD ====================

  const openCategoryDialog = (category = null) => {
    if (category) {
      setCategoryForm(category);
      setEditingCategory(category);
    } else {
      setCategoryForm({
        name: '',
        description: '',
        image_url: '',
        sort_order: categories.length,
        is_active: true
      });
      setEditingCategory(null);
    }
    setCategoryDialog(true);
  };

  const saveCategory = async () => {
    setSaving(true);
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/menu-management/categories/${editingCategory.id}`,
          categoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Category updated');
      } else {
        await axios.post(
          `${API}/menu-management/categories`,
          categoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Category created');
      }
      setCategoryDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await axios.delete(`${API}/menu-management/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const toggleCategoryActive = async (category) => {
    try {
      await axios.put(
        `${API}/menu-management/categories/${category.id}`,
        { is_active: !category.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(category.is_active ? 'Category hidden' : 'Category shown');
      fetchData();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  // ==================== MENU ITEM CRUD ====================

  const openItemDialog = (item = null) => {
    if (item) {
      setItemForm({
        ...item,
        price: item.price?.toString() || '',
        calories: item.calories?.toString() || ''
      });
      setEditingItem(item);
    } else {
      setItemForm({
        category_id: categories[0]?.id || '',
        name: '',
        description: '',
        price: '',
        image_url: '',
        is_available: true,
        is_popular: false,
        is_spicy: false,
        is_vegetarian: false,
        is_vegan: false,
        is_gluten_free: false,
        calories: '',
        prep_time_minutes: 15,
        sort_order: 0
      });
      setEditingItem(null);
    }
    setItemDialog(true);
  };

  const saveItem = async () => {
    setSaving(true);
    try {
      const payload = {
        ...itemForm,
        price: parseFloat(itemForm.price),
        calories: itemForm.calories ? parseInt(itemForm.calories) : null
      };

      if (editingItem) {
        await axios.put(
          `${API}/menu-management/items/${editingItem.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Item updated');
      } else {
        await axios.post(
          `${API}/menu-management/items`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Item created');
      }
      setItemDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save item:', error);
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/menu-management/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete item:', error);
      toast.error('Failed to delete item');
    }
  };

  const toggleItemAvailable = async (item) => {
    try {
      await axios.put(
        `${API}/menu-management/items/${item.id}`,
        { is_available: !item.is_available },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(item.is_available ? 'Item hidden from menu' : 'Item shown on menu');
      fetchData();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  // ==================== MODIFIER CRUD ====================

  const openModifierDialog = (item) => {
    setSelectedItemForModifier(item);
    setModifierForm({
      name: '',
      is_required: false,
      min_selections: 0,
      max_selections: 1,
      options: [{ name: '', price: 0 }]
    });
    setEditingModifier(null);
    setModifierDialog(true);
  };

  const addModifierOption = () => {
    setModifierForm(prev => ({
      ...prev,
      options: [...prev.options, { name: '', price: 0 }]
    }));
  };

  const removeModifierOption = (index) => {
    setModifierForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateModifierOption = (index, field, value) => {
    setModifierForm(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = {
        ...newOptions[index],
        [field]: field === 'price' ? parseFloat(value) || 0 : value
      };
      return { ...prev, options: newOptions };
    });
  };

  const saveModifier = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/menu-management/items/${selectedItemForModifier.id}/modifiers`,
        modifierForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Modifier group created');
      setModifierDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save modifier:', error);
      toast.error('Failed to save modifier');
    } finally {
      setSaving(false);
    }
  };

  const deleteModifier = async (modifierId) => {
    if (!confirm('Delete this modifier group?')) return;
    
    try {
      await axios.delete(`${API}/menu-management/modifiers/${modifierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modifier deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete modifier:', error);
      toast.error('Failed to delete modifier');
    }
  };

  // ==================== RENDER ====================

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Menu Management</h1>
        <p className="text-sm text-gray-500 mt-1">Manage categories, items, pricing, and modifiers</p>
      </div>

      {/* Search & Actions */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search menu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {activeTab === 'categories' && (
            <Button onClick={() => openCategoryDialog()} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          )}
          {activeTab === 'items' && (
            <Button onClick={() => openItemDialog()} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="categories">
            Categories ({categories.length})
          </TabsTrigger>
          <TabsTrigger value="items">
            Menu Items ({items.length})
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-3">
          {filteredCategories.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-500">No categories yet. Create your first category to get started.</p>
                <Button onClick={() => openCategoryDialog()} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Category
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredCategories.map(category => (
              <Card key={category.id} className={`${!category.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center text-2xl">
                      {category.image_url ? (
                        <img src={category.image_url} alt={category.name} className="h-full w-full object-cover rounded-lg" />
                      ) : (
                        '📂'
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{category.name}</h3>
                        {!category.is_active && <Badge variant="outline">Hidden</Badge>}
                        <Badge variant="secondary">{category.item_count} items</Badge>
                      </div>
                      {category.description && (
                        <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                      )}
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openCategoryDialog(category)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleCategoryActive(category)}>
                          {category.is_active ? (
                            <><EyeOff className="h-4 w-4 mr-2" />Hide</>
                          ) : (
                            <><Eye className="h-4 w-4 mr-2" />Show</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => deleteCategory(category.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items" className="space-y-3">
          {filteredItems.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-500">No menu items yet. Add your first item to get started.</p>
                <Button onClick={() => openItemDialog()} className="mt-4" disabled={categories.length === 0}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Menu Item
                </Button>
                {categories.length === 0 && (
                  <p className="text-xs text-gray-400 mt-2">Create a category first</p>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredItems.map(item => (
              <Card key={item.id} className={`${!item.is_available ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="h-20 w-20 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-3xl">🍽️</div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900 dark:text-white">{item.name}</h3>
                            {item.is_popular && <Badge className="bg-yellow-500">Popular</Badge>}
                            {!item.is_available && <Badge variant="outline">Unavailable</Badge>}
                          </div>
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1">{item.description}</p>
                          
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <span className="text-lg font-bold text-blue-600">${item.price?.toFixed(2)}</span>
                            {item.is_spicy && <Badge variant="outline" className="text-red-600"><Flame className="h-3 w-3 mr-1" />Spicy</Badge>}
                            {item.is_vegetarian && <Badge variant="outline" className="text-green-600"><Leaf className="h-3 w-3 mr-1" />Vegetarian</Badge>}
                            {item.is_vegan && <Badge variant="outline" className="text-green-700">Vegan</Badge>}
                            {item.is_gluten_free && <Badge variant="outline">GF</Badge>}
                            {item.calories && <Badge variant="secondary">{item.calories} cal</Badge>}
                            {item.modifiers?.length > 0 && (
                              <Badge variant="secondary">{item.modifiers.length} modifier groups</Badge>
                            )}
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="flex-shrink-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openItemDialog(item)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Item
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openModifierDialog(item)}>
                              <Settings className="h-4 w-4 mr-2" />
                              Add Modifiers
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleItemAvailable(item)}>
                              {item.is_available ? (
                                <><EyeOff className="h-4 w-4 mr-2" />Hide from Menu</>
                              ) : (
                                <><Eye className="h-4 w-4 mr-2" />Show on Menu</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => deleteItem(item.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update category information' : 'Add a new menu category'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="cat-name">Category Name *</Label>
              <Input
                id="cat-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Appetizers, Main Dishes"
              />
            </div>

            <div>
              <Label htmlFor="cat-desc">Description</Label>
              <Textarea
                id="cat-desc"
                value={categoryForm.description || ''}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="cat-img">Image URL (optional)</Label>
              <Input
                id="cat-img"
                value={categoryForm.image_url || ''}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="cat-active">Active (visible to customers)</Label>
              <Switch
                id="cat-active"
                checked={categoryForm.is_active}
                onCheckedChange={(checked) => setCategoryForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog(false)}>Cancel</Button>
            <Button onClick={saveCategory} disabled={saving || !categoryForm.name}>
              {saving ? 'Saving...' : 'Save Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Menu Item' : 'Create Menu Item'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="item-name">Item Name *</Label>
                <Input
                  id="item-name"
                  value={itemForm.name}
                  onChange={(e) => setItemForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Grilled Salmon"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="item-desc">Description</Label>
                <Textarea
                  id="item-desc"
                  value={itemForm.description || ''}
                  onChange={(e) => setItemForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the dish..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="item-cat">Category *</Label>
                <Select
                  value={itemForm.category_id?.toString()}
                  onValueChange={(value) => setItemForm(prev => ({ ...prev, category_id: parseInt(value) }))}
                >
                  <SelectTrigger id="item-cat">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="item-price">Price ($) *</Label>
                <Input
                  id="item-price"
                  type="number"
                  step="0.01"
                  value={itemForm.price}
                  onChange={(e) => setItemForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="item-calories">Calories (optional)</Label>
                <Input
                  id="item-calories"
                  type="number"
                  value={itemForm.calories || ''}
                  onChange={(e) => setItemForm(prev => ({ ...prev, calories: e.target.value }))}
                  placeholder="e.g., 520"
                />
              </div>

              <div>
                <Label htmlFor="item-prep">Prep Time (minutes)</Label>
                <Input
                  id="item-prep"
                  type="number"
                  value={itemForm.prep_time_minutes}
                  onChange={(e) => setItemForm(prev => ({ ...prev, prep_time_minutes: parseInt(e.target.value) || 15 }))}
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="item-img">Image URL (optional)</Label>
                <Input
                  id="item-img"
                  value={itemForm.image_url || ''}
                  onChange={(e) => setItemForm(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Item Attributes</Label>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="item-available">Available</Label>
                  <Switch
                    id="item-available"
                    checked={itemForm.is_available}
                    onCheckedChange={(checked) => setItemForm(prev => ({ ...prev, is_available: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="item-popular">Popular</Label>
                  <Switch
                    id="item-popular"
                    checked={itemForm.is_popular}
                    onCheckedChange={(checked) => setItemForm(prev => ({ ...prev, is_popular: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="item-spicy">Spicy</Label>
                  <Switch
                    id="item-spicy"
                    checked={itemForm.is_spicy}
                    onCheckedChange={(checked) => setItemForm(prev => ({ ...prev, is_spicy: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="item-veg">Vegetarian</Label>
                  <Switch
                    id="item-veg"
                    checked={itemForm.is_vegetarian}
                    onCheckedChange={(checked) => setItemForm(prev => ({ ...prev, is_vegetarian: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="item-vegan">Vegan</Label>
                  <Switch
                    id="item-vegan"
                    checked={itemForm.is_vegan}
                    onCheckedChange={(checked) => setItemForm(prev => ({ ...prev, is_vegan: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="item-gf">Gluten Free</Label>
                  <Switch
                    id="item-gf"
                    checked={itemForm.is_gluten_free}
                    onCheckedChange={(checked) => setItemForm(prev => ({ ...prev, is_gluten_free: checked }))}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(false)}>Cancel</Button>
            <Button 
              onClick={saveItem} 
              disabled={saving || !itemForm.name || !itemForm.price || !itemForm.category_id}
            >
              {saving ? 'Saving...' : 'Save Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modifier Dialog */}
      <Dialog open={modifierDialog} onOpenChange={setModifierDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Modifier Group</DialogTitle>
            <DialogDescription>
              {selectedItemForModifier?.name ? `Adding modifiers to: ${selectedItemForModifier.name}` : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="mod-name">Group Name *</Label>
              <Input
                id="mod-name"
                value={modifierForm.name}
                onChange={(e) => setModifierForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Size, Add-Ons, Cooking Temperature"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="mod-req">Required</Label>
                <Switch
                  id="mod-req"
                  checked={modifierForm.is_required}
                  onCheckedChange={(checked) => setModifierForm(prev => ({ ...prev, is_required: checked }))}
                />
              </div>
              <div>
                <Label htmlFor="mod-min">Min Selections</Label>
                <Input
                  id="mod-min"
                  type="number"
                  value={modifierForm.min_selections}
                  onChange={(e) => setModifierForm(prev => ({ ...prev, min_selections: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label htmlFor="mod-max">Max Selections</Label>
                <Input
                  id="mod-max"
                  type="number"
                  value={modifierForm.max_selections}
                  onChange={(e) => setModifierForm(prev => ({ ...prev, max_selections: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium">Options</Label>
                <Button type="button" variant="outline" size="sm" onClick={addModifierOption}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Option
                </Button>
              </div>

              <div className="space-y-2">
                {modifierForm.options.map((option, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="Option name"
                      value={option.name}
                      onChange={(e) => updateModifierOption(index, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={option.price}
                      onChange={(e) => updateModifierOption(index, 'price', e.target.value)}
                      className="w-24"
                    />
                    {modifierForm.options.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeModifierOption(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModifierDialog(false)}>Cancel</Button>
            <Button 
              onClick={saveModifier} 
              disabled={saving || !modifierForm.name || modifierForm.options.some(o => !o.name)}
            >
              {saving ? 'Saving...' : 'Save Modifier Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
